// Lightweight per-route SEO for this SPA: sets <title>, meta description,
// keywords, canonical URL and Open Graph tags on navigation. (No SSR — Google
// executes JS.) Static pages pass only a `pageKey`; their built-in defaults live
// in SEO_PAGES below and can be overridden per-page from Admin → SEO (stored in
// the Firestore `settings/seo` doc, keyed by page key).
import { useEffect } from "react";
import { usePageSeo } from "../hooks/useCatalog";

const SITE = "https://halalgifhampers.com";
const BRAND = "HALALGIFHAMPERS";
const DEFAULT_TITLE = "HALALGIFHAMPERS — Premium Halal Gift Hampers Singapore";
const DEFAULT_DESC =
  "Premium halal gift hampers for weddings, newborns, birthdays, festive seasons and corporate gifting. Islandwide Singapore delivery.";
const DEFAULT_KEYWORDS =
  "halal gift hampers, halal hampers singapore, gift hampers singapore, halal gifts, corporate gift hampers";

// Canonical registry of the static, SEO-manageable pages. The Admin → SEO panel
// reads this to know which pages exist and their built-in defaults; each page
// passes only its `pageKey` to useSeo(). Admin overrides (settings/seo) win.
export const SEO_PAGES = [
  {
    key: "home",
    label: "Home",
    path: "/",
    title: DEFAULT_TITLE,
    description: DEFAULT_DESC,
    keywords: DEFAULT_KEYWORDS,
  },
  {
    key: "shop",
    label: "Shop",
    path: "/shop",
    title: "Shop All Halal Gift Hampers",
    description:
      "Browse our full range of premium halal gift hampers — wedding, newborn, birthday, corporate, festive and more. Islandwide Singapore delivery.",
    keywords:
      "shop halal hampers, buy gift hampers singapore, halal gift baskets, festive hampers, corporate hampers",
  },
  {
    key: "about",
    label: "About Us",
    path: "/about",
    title: "About Us",
    description:
      "HALALGIFHAMPERS is a Singapore-based curator of premium halal-friendly gift hampers for weddings, newborns, festive occasions and corporate gifting.",
    keywords:
      "about halalgifhampers, halal hamper company singapore, halal gift curator",
  },
  {
    key: "contact",
    label: "Contact Us",
    path: "/contact",
    title: "Contact Us",
    description:
      "Get in touch with HALALGIFHAMPERS — WhatsApp, email or visit us at Midview Building, Singapore. Custom and corporate hamper enquiries welcome.",
    keywords:
      "contact halalgifhampers, halal hamper enquiry, corporate gifting singapore",
  },
  {
    key: "faq",
    label: "FAQ",
    path: "/faq",
    title: "FAQ",
    description:
      "Answers about ordering, delivery, payment and halal assurance for HALALGIFHAMPERS gift hampers in Singapore.",
    keywords:
      "halal hamper faq, gift hamper delivery singapore, halal gift questions",
  },
  {
    key: "privacy",
    label: "Privacy Policy",
    path: "/privacy",
    title: "Privacy Policy",
    description:
      "How HALALGIFHAMPERS collects, uses and protects your personal information.",
    keywords: "privacy policy, halalgifhampers privacy",
  },
  {
    key: "terms",
    label: "Terms & Conditions",
    path: "/terms",
    title: "Terms & Conditions",
    description:
      "Terms and conditions for ordering halal gift hampers from HALALGIFHAMPERS Singapore.",
    keywords: "terms and conditions, halalgifhampers terms",
  },
];

const PAGE_MAP = Object.fromEntries(SEO_PAGES.map((p) => [p.key, p]));

function setMeta(selector, attr, value) {
  if (!value) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const [key, val] = selector.replace(/meta\[|\]/g, "").split("=");
    el.setAttribute(key, val.replace(/["']/g, ""));
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * @param {{pageKey?:string, title?:string, description?:string, keywords?:string, path?:string, image?:string}} opts
 */
export function useSeo({ pageKey, title, description, keywords, path, image } = {}) {
  // Admin overrides from Firestore (cached). Empty until loaded / if unset.
  const { data: overrides = {} } = usePageSeo();
  const base = (pageKey && PAGE_MAP[pageKey]) || {};
  const ov = (pageKey && overrides && overrides[pageKey]) || {};

  // Precedence: admin override → explicit prop → page default → site default.
  const resolvedTitle = ov.title || title || base.title || DEFAULT_TITLE;
  const resolvedDesc = ov.description || description || base.description || DEFAULT_DESC;
  const resolvedKeywords = ov.keywords || keywords || base.keywords || DEFAULT_KEYWORDS;
  const resolvedPath = path || base.path;

  useEffect(() => {
    // Admin-authored titles may already include the brand — don't double it.
    const finalTitle = resolvedTitle.includes(BRAND)
      ? resolvedTitle
      : `${resolvedTitle} | ${BRAND}`;
    const url = `${SITE}${resolvedPath || window.location.pathname}`;

    document.title = finalTitle;
    setMeta('meta[name="description"]', "content", resolvedDesc);
    setMeta('meta[name="keywords"]', "content", resolvedKeywords);
    setMeta('meta[property="og:title"]', "content", finalTitle);
    setMeta('meta[property="og:description"]', "content", resolvedDesc);
    setMeta('meta[property="og:url"]', "content", url);
    if (image) setMeta('meta[property="og:image"]', "content", image);
    setCanonical(url);

    return () => {
      document.title = DEFAULT_TITLE;
    };
  }, [resolvedTitle, resolvedDesc, resolvedKeywords, resolvedPath, image]);
}
